(function () {
  class CartRequestError extends Error {
    constructor(message, details) {
      super(message);
      this.name = 'CartRequestError';
      Object.assign(this, details);
    }
  }

  function getSectionDOM(html, selector) {
    if (!html) return null;
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector || '.shopify-section');
  }

  function getSectionInnerHTML(html, selector) {
    return getSectionDOM(html, selector)?.innerHTML || '';
  }

  function getShopifyMessage(payload, fallback) {
    if (!payload) return fallback;

    const values = [payload.errors, payload.description, payload.message];
    for (const value of values) {
      if (typeof value === 'string' && value.trim()) return value.trim();
      if (Array.isArray(value) && value.length) return value.filter(Boolean).join(' ');
      if (value && typeof value === 'object') {
        const message = Object.values(value).flat().filter(Boolean).join(' ');
        if (message) return message;
      }
    }

    return fallback;
  }

  function classifyError(status, payload, parsed) {
    const message = getShopifyMessage(payload, '').toLowerCase();

    if (!parsed) return 'unexpected-response';
    if (status >= 500) return 'server';
    if (status === 422) {
      if (/stock|inventory|disponible|available/.test(message)) return 'stock';
      return 'quantity';
    }
    if (/section/.test(message)) return 'section';
    return 'request';
  }

  function isDevelopment() {
    return Boolean(
      window.Shopify?.designMode ||
      /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname)
    );
  }

  function responseSummary(payload, raw) {
    if (!payload) return String(raw || '').slice(0, 300);
    return {
      errors: payload.errors,
      description: payload.description,
      message: payload.message,
      item_count: payload.item_count,
      sections: payload.sections ? Object.keys(payload.sections) : [],
    };
  }

  function logRequestError(error, context) {
    if (!isDevelopment() || !window.console?.error) return;
    console.error('[RetroParty cart]', {
      url: context.url,
      status: error.status || 0,
      response: error.response,
      line: context.line,
      lineKey: context.lineKey,
      requestedQuantity: context.quantity,
      type: error.type,
    });
  }

  async function requestJSON(url, options, context) {
    let response;

    try {
      response = await fetch(url, options);
    } catch (cause) {
      const error = new CartRequestError('Impossible de contacter Shopify. Vérifiez votre connexion puis réessayez.', {
        type: cause?.name === 'AbortError' ? 'aborted' : 'network',
        status: 0,
        response: null,
        cause,
      });
      logRequestError(error, { ...context, url });
      throw error;
    }

    const raw = await response.text();
    let payload = null;
    let parsed = true;

    try {
      payload = raw ? JSON.parse(raw) : {};
    } catch (cause) {
      parsed = false;
    }

    if (!response.ok || !parsed || payload?.errors) {
      const fallback = !parsed
        ? 'Shopify a renvoyé une réponse inattendue. La quantité précédente a été restaurée.'
        : response.status >= 500
          ? 'Le panier est momentanément indisponible. Veuillez réessayer.'
          : window.cartStrings?.error || 'Impossible de mettre à jour le panier.';

      const error = new CartRequestError(getShopifyMessage(payload, fallback), {
        type: classifyError(response.status, payload, parsed),
        status: response.status,
        response: responseSummary(payload, raw),
        payload,
      });
      logRequestError(error, { ...context, url });
      throw error;
    }

    return { data: payload, status: response.status };
  }

  function renderSections(state, definitions) {
    if (!state?.sections) return [];

    const rendered = [];
    definitions.forEach((definition) => {
      const html = state.sections[definition.section];
      const target = document.querySelector(definition.target);
      if (!html || !target) return;

      const source = getSectionDOM(html, definition.source);
      if (!source) return;

      target.innerHTML = source.innerHTML;
      rendered.push(definition.section);
    });

    return rendered;
  }

  function syncHeaderCart() {
    const drawer = document.querySelector('cart-drawer');
    drawer?.syncCartDrawerTriggers?.();
    drawer?.setHeaderCartIconAccessibility?.();
  }

  function syncHeaderCartSoon() {
    window.requestAnimationFrame(syncHeaderCart);
  }

  function syncAfterRender(cartState) {
    syncHeaderCartSoon();
    window.RPFreeShipping?.updateFromCart?.(cartState);
  }

  window.RetroPartyCartSync = {
    CartRequestError,
    getSectionDOM,
    getSectionInnerHTML,
    getShopifyMessage,
    requestJSON,
    renderSections,
    syncAfterRender,
    syncHeaderCart,
    syncHeaderCartSoon,
  };
})();